import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { Link } from 'react-router-dom';
import { useStakingStore, StakingPosition } from '../../state/stakingStore';
import { toast } from 'sonner';
import { ChartBarIcon, FireIcon, LockClosedIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const TOKEN_DECIMALS = 9;

function formatTokenAmount(raw: string): string {
  const num = Number(raw) / 10 ** TOKEN_DECIMALS;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatCooldownRemaining(cooldownEnd: string): string {
  const now = new Date();
  const end = new Date(cooldownEnd);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ready';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m`;
}

function formatSolAmount(lamports: string): string {
  const sol = Number(lamports) / 1e9;
  return `${sol.toFixed(4)} SOL`;
}

export const StakingPage: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const [stakeAmount, setStakeAmount] = useState('');

  const {
    positions,
    totalStaked,
    tier,
    multiplier,
    tiers,
    buybacks,
    protocolStats,
    loading,
    fetchPositions,
    fetchTiers,
    fetchBuybacks,
    fetchProtocolStats,
    stake,
    requestUnstake,
    completeUnstake,
  } = useStakingStore();

  useEffect(() => {
    fetchTiers();
    fetchBuybacks();
    fetchProtocolStats();
  }, [fetchTiers, fetchBuybacks, fetchProtocolStats]);

  useEffect(() => {
    if (wallet) {
      fetchPositions(wallet);
    }
  }, [wallet, fetchPositions]);

  // Find next tier
  const currentTierIndex = tiers.findIndex((t) => t.name === tier);
  const nextTier = currentTierIndex > 0 ? tiers[currentTierIndex - 1] : null;

  // Progress to next tier
  const totalStakedNum = Number(totalStaked);
  const currentTierThreshold =
    currentTierIndex >= 0 ? Number(tiers[currentTierIndex].tokensRequired) : 0;
  const nextTierThreshold = nextTier ? Number(nextTier.tokensRequired) : currentTierThreshold * 2;
  const progressPct =
    nextTierThreshold > currentTierThreshold
      ? Math.min(
          100,
          ((totalStakedNum - currentTierThreshold) / (nextTierThreshold - currentTierThreshold)) *
            100,
        )
      : 100;

  const handleStake = async () => {
    if (!wallet || !stakeAmount) return;
    const amount = Number(stakeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid token amount');
      return;
    }
    const rawAmount = BigInt(Math.round(amount * 10 ** TOKEN_DECIMALS)).toString();
    try {
      await stake(wallet, rawAmount);
      toast.success(`Staked ${amount.toLocaleString()} tokens`);
      setStakeAmount('');
    } catch {
      toast.error('Failed to stake tokens');
    }
  };

  const handleRequestUnstake = async (positionId: number) => {
    if (!wallet) return;
    try {
      await requestUnstake(wallet, positionId);
      toast.success('Unstake requested. 7-day cooldown started.');
    } catch {
      toast.error('Failed to request unstake');
    }
  };

  const handleCompleteUnstake = async (positionId: number) => {
    if (!wallet) return;
    try {
      await completeUnstake(wallet, positionId);
      toast.success('Tokens unstaked successfully');
    } catch {
      toast.error('Cooldown not yet elapsed');
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8 text-center"
      >
        <h1 className="neon-text font-display text-3xl font-bold">STAKING</h1>
        <p className="mt-2 text-white/50">Stake tokens to boost your season points multiplier</p>
      </motion.div>

      {/* Your Staking Overview */}
      {wallet && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="arcade-panel pixel-corners mb-6 rounded-xl border border-arcade-cyan/30 bg-arcade-cyan/5 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-pixel text-xs text-white/60">YOUR TIER</div>
              <div className="neon-text font-display text-2xl">{tier}</div>
            </div>
            <div className="text-right">
              <div className="font-pixel text-xs text-white/60">POINTS MULTIPLIER</div>
              <div className="font-display text-2xl text-arcade-cyan">{multiplier}x</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="font-pixel text-xs text-white/50">
              Total Staked: {formatTokenAmount(totalStaked)} tokens
            </div>
            {nextTier && (
              <div className="mt-2">
                <div className="flex justify-between font-pixel text-[10px] text-white/40">
                  <span>{tier}</span>
                  <span>
                    {nextTier.name} ({nextTier.multiplier}x)
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-arcade-cyan to-arcade-purple transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {!wallet && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="arcade-panel pixel-corners mb-6 rounded-xl p-6 text-center"
        >
          <p className="font-arcade text-white/60">Connect your wallet to stake tokens</p>
        </motion.div>
      )}

      {/* Stake Form */}
      {wallet && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="arcade-panel pixel-corners mb-6 rounded-xl p-5"
        >
          <h2 className="mb-4 font-display text-lg text-white/80">Stake Tokens</h2>
          <div className="flex gap-3">
            <input
              type="number"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              placeholder="Amount of tokens"
              className="flex-1 rounded-lg border border-white/20 bg-black/50 px-4 py-2.5 font-arcade text-sm text-white placeholder:text-white/30 focus:border-arcade-cyan focus:outline-none"
            />
            <button
              onClick={handleStake}
              disabled={loading || !stakeAmount}
              className="btn-press rounded-lg border border-arcade-cyan/50 bg-arcade-cyan/20 px-6 py-2.5 font-display text-sm uppercase text-arcade-cyan transition hover:bg-arcade-cyan/30 disabled:opacity-50"
            >
              {loading ? 'Staking...' : 'Stake'}
            </button>
          </div>
        </motion.div>
      )}

      {/* Active Positions */}
      {wallet && positions.length > 0 && (
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-6">
          <h2 className="mb-3 font-display text-lg text-white/80">Active Positions</h2>
          <div className="space-y-3">
            {positions.map((pos: StakingPosition) => {
              const isUnstaking = pos.unstakeRequestedAt != null;
              const cooldownPassed =
                pos.unstakeCooldownEnd && new Date(pos.unstakeCooldownEnd) <= new Date();

              return (
                <div
                  key={pos.id}
                  className="arcade-panel flex items-center justify-between rounded-lg p-4"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                        isUnstaking ? 'bg-arcade-yellow/20' : 'bg-arcade-cyan/20'
                      }`}
                    >
                      {isUnstaking ? (
                        <ArrowPathIcon className="h-4 w-4 text-arcade-yellow" />
                      ) : (
                        <LockClosedIcon className="h-4 w-4 text-arcade-cyan" />
                      )}
                    </div>
                    <div>
                      <div className="font-arcade text-sm text-white">
                        {formatTokenAmount(pos.amountTokens)} tokens
                      </div>
                      <div className="font-pixel text-[10px] text-white/40">
                        Staked {new Date(pos.stakedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {!isUnstaking ? (
                    <button
                      onClick={() => handleRequestUnstake(pos.id)}
                      className="rounded-lg border border-arcade-pink/30 bg-arcade-pink/10 px-3 py-1.5 font-pixel text-xs text-arcade-pink transition hover:bg-arcade-pink/20"
                    >
                      Unstake
                    </button>
                  ) : cooldownPassed ? (
                    <button
                      onClick={() => handleCompleteUnstake(pos.id)}
                      className="rounded-lg border border-arcade-green/30 bg-arcade-green/10 px-3 py-1.5 font-pixel text-xs text-arcade-green transition hover:bg-arcade-green/20"
                    >
                      Withdraw
                    </button>
                  ) : (
                    <div className="text-right">
                      <div className="font-pixel text-xs text-arcade-yellow">Cooling down</div>
                      <div className="font-pixel text-[10px] text-white/40">
                        {formatCooldownRemaining(pos.unstakeCooldownEnd!)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Tier Progression */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <h2 className="mb-3 font-display text-lg text-white/80">Staking Tiers</h2>
        <div className="arcade-panel pixel-corners overflow-hidden rounded-xl">
          <div className="grid grid-cols-3 gap-2 border-b border-white/20 bg-black/30 px-3 sm:px-4 py-3 font-pixel text-xs text-white/60">
            <div>Tier</div>
            <div>Tokens Required</div>
            <div className="text-right">Multiplier</div>
          </div>
          {[...tiers].reverse().map((t) => {
            const isCurrent = t.name === tier;
            return (
              <div
                key={t.name}
                className={`grid grid-cols-3 items-center gap-2 border-b border-white/10 px-3 sm:px-4 py-3 ${
                  isCurrent ? 'bg-arcade-cyan/10' : ''
                }`}
              >
                <div>
                  <span
                    className={`font-display text-sm ${isCurrent ? 'text-arcade-cyan' : 'text-white/70'}`}
                  >
                    {t.name}
                  </span>
                  {isCurrent && (
                    <span className="ml-2 font-pixel text-[10px] text-arcade-cyan">CURRENT</span>
                  )}
                </div>
                <div className="font-pixel text-xs text-white/50">
                  {formatTokenAmount(t.tokensRequired)}
                </div>
                <div
                  className={`text-right font-display text-lg ${
                    isCurrent ? 'neon-text' : 'text-white/60'
                  }`}
                >
                  {t.multiplier}x
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Protocol Transparency */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <h2 className="mb-3 font-display text-lg text-white/80">Protocol Stats</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="arcade-panel rounded-lg p-4 text-center">
            <FireIcon className="mx-auto mb-2 h-6 w-6 text-arcade-pink" />
            <div className="font-pixel text-xs text-white/50">Total Burned</div>
            <div className="font-display text-lg text-arcade-pink">
              {protocolStats ? formatTokenAmount(protocolStats.totalBurned) : '—'}
            </div>
          </div>
          <div className="arcade-panel rounded-lg p-4 text-center">
            <LockClosedIcon className="mx-auto mb-2 h-6 w-6 text-arcade-cyan" />
            <div className="font-pixel text-xs text-white/50">Total Staked</div>
            <div className="font-display text-lg text-arcade-cyan">
              {protocolStats ? formatTokenAmount(protocolStats.totalStaked) : '—'}
            </div>
          </div>
          <div className="arcade-panel rounded-lg p-4 text-center">
            <ChartBarIcon className="mx-auto mb-2 h-6 w-6 text-arcade-green" />
            <div className="font-pixel text-xs text-white/50">Protocol Revenue</div>
            <div className="font-display text-lg text-arcade-green">
              {protocolStats?.totalRevenue?.[0]
                ? formatSolAmount(protocolStats.totalRevenue[0].total)
                : '—'}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Buyback History */}
      {buybacks.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="mb-3 font-display text-lg text-white/80">Buyback &amp; Burn History</h2>
          <div className="space-y-2">
            {buybacks.slice(0, 10).map((b) => (
              <div
                key={b.id}
                className="arcade-panel flex items-center justify-between rounded-lg p-3"
              >
                <div>
                  <div className="font-arcade text-sm text-white">
                    {formatSolAmount(b.amountSolLamports)} spent
                  </div>
                  <div className="font-pixel text-[10px] text-white/40">
                    {new Date(b.executedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-pixel text-xs text-arcade-pink">
                    {formatTokenAmount(b.tokensBurned)} burned
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Back to Home */}
      <div className="mt-8 text-center">
        <Link
          to="/"
          className="font-pixel text-sm text-white/60 transition-colors hover:text-white"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
};
